import { useCallback, useEffect, useState } from "react";

import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { NoteAdd as NoteAddIcon } from "@mui/icons-material";
import { Grid, IconButton, Stack, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

import { useAuth, useOutletApi } from "@/features/authentication";
import { OutletRoleEnum, UserOutletsSelect } from "@/features/outlets";
import { PermissionEnum } from "@/features/permissions";
import { formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import {
  CustomDateRangePicker,
  PageContainer,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";
import {
  useDateRangeFilter,
  useSearchFilter,
  useServerPagination,
} from "@/hooks";

// Payments other outlets raised against this one and that are still waiting to
// be mirrored as a sales payment. Seller-scoped: only the hub sees this queue,
// and until it converts, the money the store already sent is not on its books.
export const IncomingPurchasePayments = () => {
  const { outlet, changeOutlet, isHubOutlet, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const api = useOutletApi();
  const [purchasePayments, setPurchasePayments] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    setRowCount,
    paginationParams,
    dataGridPaginationProps,
    resetParams,
  } = useServerPagination();
  const { keyword, searchProps } = useSearchFilter({ resetParams });
  const { dateRangeProps, dateParams } = useDateRangeFilter({ resetParams });

  const loadIncomingPurchasePayments = useCallback(async () => {
    if (!outlet) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await api.get("purchase-payments/incoming", {
        params: {
          ...paginationParams,
          ...dateParams,
          // This queue filters by reference number, not a free-text keyword.
          ...(keyword && { referenceNumber: keyword }),
        },
      });
      setPurchasePayments(data);
      setRowCount(pageInfo.totalItems);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat pembayaran pembelian masuk"));
    } finally {
      setLoading(false);
    }
  }, [
    api,
    outlet,
    paginationParams,
    dateParams,
    keyword,
    setRowCount,
    showSnackbar,
  ]);

  useEffect(() => {
    loadIncomingPurchasePayments();
  }, [loadIncomingPurchasePayments]);

  return (
    <PageContainer
      title="Pembayaran dari Pembeli"
      breadcrumbs={[]}
      maxWidth={false}
    >
      <Stack spacing={3}>
        <Grid
          container
          spacing={2}
          sx={{ justifyContent: "flex-end", alignItems: "end" }}
        >
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <UserOutletsSelect
              value={outlet}
              onChange={changeOutlet}
              // Only a hub is ever the seller on an internal payment, so the
              // other outlets would just select an always-empty queue.
              allowedRoles={[OutletRoleEnum.HUB]}
              TextFieldProps={{
                label: "Penjual",
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <SearchTextField
              fullWidth
              placeholder="Cari No. Referensi"
              {...searchProps}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 12, lg: 6 }}>
            <CustomDateRangePicker {...dateRangeProps} />
          </Grid>
        </Grid>
        <DataGrid
          rows={purchasePayments}
          loading={loading}
          getRowHeight={() => "auto"}
          slots={{
            noRowsOverlay: () => (
              <Stack
                sx={{
                  height: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 3,
                }}
              >
                {isHubOutlet
                  ? "Tidak ada pembayaran pembelian yang menunggu konversi"
                  : "Pembayaran pembelian masuk hanya diterima oleh outlet Hub"}
              </Stack>
            ),
          }}
          columns={[
            {
              field: "referenceNumber",
              headerName: "Nomor #",
              flex: 1,
              sortable: false,
            },
            {
              field: "transactionDate",
              headerName: "Tanggal",
              flex: 1,
              sortable: false,
              // The age matters here: until this converts, the paying outlet's
              // money is not recognised on the hub's books.
              valueGetter: (transactionDate) => {
                const today = dayjs().startOf("day");
                const transaction = dayjs(transactionDate).startOf("day");
                const age = today.diff(transaction, "day");
                return age > 0
                  ? `${formatDate(transactionDate)} (${age}d)`
                  : formatDate(transactionDate);
              },
            },
            {
              field: "buyer",
              headerName: "Outlet Pembayar",
              flex: 1.5,
              sortable: false,
              valueGetter: (buyer) => buyer?.name ?? "-",
            },
            {
              field: "grandTotal",
              headerName: "Total",
              flex: 1,
              type: "number",
              sortable: false,
              valueGetter: (value) => formatNumber(value),
            },
            {
              field: "note",
              headerName: "Catatan",
              flex: 1.5,
              sortable: false,
              valueGetter: (note) => note ?? "-",
            },
            {
              field: "actions",
              type: "actions",
              headerName: "Aksi",
              getActions: ({ row: { referenceNumber } }) => {
                const actions = [];
                if (hasPermission(PermissionEnum.CREATE_SALES_PAYMENTS)) {
                  actions.push(
                    <Tooltip key="convert" title="Buat Penerimaan Penjualan">
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate(`${referenceNumber}/create-sales-payment`)
                        }
                      >
                        <NoteAddIcon />
                      </IconButton>
                    </Tooltip>
                  );
                }
                return actions;
              },
            },
          ]}
          {...dataGridPaginationProps}
          disableRowSelectionOnClick
        />
      </Stack>
    </PageContainer>
  );
};
